import React, { useEffect, useRef, useState } from 'react'
import Markdown from '../../components/Markdown'
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, CircularProgress, Collapse,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, Tab, Tabs, TextField, Typography,
} from '@mui/material'
import ArrowBackIcon          from '@mui/icons-material/ArrowBack'
import CancelIcon             from '@mui/icons-material/CancelOutlined'
import ExpandMoreIcon         from '@mui/icons-material/ExpandMore'
import PlayCircleOutlinedIcon from '@mui/icons-material/PlayCircleOutlined'
import LockIcon               from '@mui/icons-material/LockOutlined'
import { useNavigate }        from 'react-router-dom'
import { supabase }           from '../../supabaseClient'
import { useAuth }            from '../../auth/AuthContext'
import { useViewerStudent }   from '../../auth/ViewAsContext'
import StudyModeFork          from '../../components/datascience/StudyModeFork'
import { Project }            from '../../types'
import ConversationThread     from '../../components/messaging/ConversationThread'
import JournalTab             from './JournalTab'

// Step 2 — Research Question (Scientific Inquiry track) and, eventually,
// Problem Statement (Engineering Design track). The structure mirrors Step 1:
// substep tabs gated by a state machine, mentor review pipeline, reflection
// at the close.

// ── Learn content (carried from the previous Step 2 placeholder) ──────────────

const LEARN_SECTIONS = [
  {
    title:   'Narrowing Your Research Topic',
    intro:   'Before you can ask a research question or build a design solution, you need to focus your topic to something specific you can actually do as a science fair project.',
    videos: [
      { title: 'Select Your Research Topic',         videoId: '849303368' },
      { title: 'Using Wikipedia to Explore a Topic', videoId: '849303833' },
    ],
  },
  {
    title:   'Drafting a Research Question',
    intro:   'A research question is a clear, specific, testable statement that points to what you want to investigate. The strongest questions identify what changes (independent variable) and what you measure (dependent variable).',
    videos: [
      { title: 'What Makes a Strong Research Question?', videoId: '849303522' },
      { title: 'Using AI to Generate Research Questions', videoId: '849303287' },
    ],
  },
  // Note: "Writing a Statement of Purpose" (Vimeo 849303772) moved out of Step 2.
  // The SoP now lives in Step 4, after the Step 3 literature review — re-add the
  // lesson there when Step 4 is built.
]

function VimeoEmbed({ videoId, title }: { videoId: string; title: string }) {
  return (
    <Box sx={{ mt: 2, textAlign: 'center' }}>
      <iframe
        title={title}
        src={`https://player.vimeo.com/video/${videoId}?title=0&portrait=0&byline=0`}
        width="640"
        height="360"
        style={{ border: 'none', maxWidth: '100%' }}
        allow="autoplay; fullscreen"
        allowFullScreen
      />
    </Box>
  )
}

function LearnTab() {
  const [openVideo, setOpenVideo] = useState<string | null>(null)
  return (
    <Box sx={{ maxWidth: 800, py: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Watch these short lessons before deciding on your research direction. They walk through narrowing
        a topic and drafting a research question.
      </Typography>
      {LEARN_SECTIONS.map((section, si) => (
        <Box key={si} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.75 }}>
            {si + 1}. {section.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            {section.intro}
          </Typography>
          {section.videos.map(v => (
            <Card key={v.videoId} variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: '12px !important' }}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
                  onClick={() => setOpenVideo(openVideo === v.videoId ? null : v.videoId)}
                >
                  <PlayCircleOutlinedIcon color="primary" fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{v.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {openVideo === v.videoId ? 'Hide' : 'Watch'}
                  </Typography>
                </Box>
                {openVideo === v.videoId && <VimeoEmbed videoId={v.videoId} title={v.title} />}
              </CardContent>
            </Card>
          ))}
          {si < LEARN_SECTIONS.length - 1 && <Divider sx={{ mt: 3 }} />}
        </Box>
      ))}
    </Box>
  )
}

// ── Substep placeholders ─────────────────────────────────────────────────────
// Each substep gets its own component so we can fill them in piece by piece.

function ResearchTypeTab({
  projectId, studentId, topic, topicDesc, trackCommitted, trackRationale,
  onTrackCommitted, onRationaleSaved, onContinue,
}: {
  projectId:        string
  studentId:        string
  topic:            string | null
  topicDesc:        string | null
  trackCommitted:   TrackKind | null
  trackRationale:   string | null
  onTrackCommitted: (track: TrackKind) => void
  onRationaleSaved: (rationale: string) => void
  onContinue:       () => void
}) {
  const [committing,   setCommitting]   = useState<TrackKind | null>(null)
  const [commitErr,    setCommitErr]    = useState<string | null>(null)
  const [helpExpanded, setHelpExpanded] = useState(false)

  async function commit(track: TrackKind) {
    if (committing || trackCommitted === track) return
    setCommitting(track)
    setCommitErr(null)
    try {
      const { error } = await supabase
        .from('research_question')
        .upsert(
          {
            project_id:      projectId,
            student_id:      studentId,
            track_committed: track,
            status:          'draft',
            updated_at:      new Date().toISOString(),
          },
          { onConflict: 'project_id' },
        )
      if (error) throw new Error(error.message)
      onTrackCommitted(track)
    } catch (e: any) {
      setCommitErr(e.message ?? 'Could not save your selection — please try again.')
    } finally {
      setCommitting(null)
    }
  }

  return (
    <Box sx={{ maxWidth: 760, pt: 2 }}>
      {/* ── Topic recap ─────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'rgba(15,43,76,0.03)' }}>
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block', lineHeight: 1.4 }}
          >
            Your research topic
          </Typography>
          <Typography
            variant={topic ? 'body1' : 'body2'}
            sx={{
              fontWeight:  topic ? 600 : 400,
              mt:          0.25,
              color:       topic ? 'text.primary' : 'text.secondary',
              fontStyle:   topic ? 'normal' : 'italic',
            }}
          >
            {topic ?? 'Topic not yet selected — return to Step 1 to choose your research topic.'}
          </Typography>
          {topicDesc && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
              {topicDesc}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* ── Education ───────────────────────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>
        Choose your research type
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.65 }}>
        Your topic can be tackled either way — both follow scientific rigor.
        More than 80% of students choose <strong>Scientific Method</strong> to investigate
        how something works. <strong>Engineering Design</strong> is for projects where you
        build a solution to a problem and measure how well it performs. If you already know
        which fits, pick below; if not, expand <strong>Help me decide</strong>.
      </Typography>

      {/* ── Help me decide (collapsible, default closed) ────────────────── */}
      <Card
        variant="outlined"
        sx={{
          mb:          2.5,
          borderColor: 'divider',
          bgcolor:     helpExpanded ? 'background.paper' : 'rgba(15,43,76,0.02)',
        }}
      >
        <CardActionArea onClick={() => setHelpExpanded(e => !e)}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Help me decide
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Not sure? Expand to see the criteria for each path and ask AI for 2 examples on your topic.
              </Typography>
            </Box>
            <ExpandMoreIcon
              sx={{
                transform:  helpExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color:      'text.secondary',
              }}
            />
          </Box>
        </CardActionArea>
        <Collapse in={helpExpanded} unmountOnExit>
          <CardContent sx={{ pt: 0 }}>
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <CriteriaColumn
                type="scientific_method"
                heading="Choose Scientific Method if…"
                bullets={[
                  'You are comparing conditions',
                  'You want to make a claim about cause/effect',
                  'Your output is knowledge',
                ]}
                projectId={projectId}
                studentId={studentId}
                topicAvailable={!!topic}
              />
              <CriteriaColumn
                type="engineering_design"
                heading="Choose Engineering Design if…"
                bullets={[
                  'You are building something new',
                  'You want to improve a real situation',
                  'Your output is a working solution',
                ]}
                projectId={projectId}
                studentId={studentId}
                topicAvailable={!!topic}
              />
            </Box>
          </CardContent>
        </Collapse>
      </Card>

      {/* ── Quick selection ─────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
        <TrackPickCard
          track="scientific_method"
          title="Scientific Method"
          summary="Investigate how something in the natural world works. You'll form a hypothesis and design an experiment to test it."
          selected={trackCommitted === 'scientific_method'}
          loading={committing === 'scientific_method'}
          disabled={committing !== null}
          onClick={() => commit('scientific_method')}
        />
        <TrackPickCard
          track="engineering_design"
          title="Engineering Design"
          summary="Build a solution to a problem. You'll specify requirements, design and build, then measure performance against those requirements."
          selected={trackCommitted === 'engineering_design'}
          loading={committing === 'engineering_design'}
          disabled={committing !== null}
          onClick={() => commit('engineering_design')}
        />
      </Box>

      {commitErr && <Alert severity="error" sx={{ mb: 2 }}>{commitErr}</Alert>}

      {/* ── Justify your direction (post-commit) ────────────────────────── */}
      {trackCommitted && (
        <JustifyPanel
          projectId={projectId}
          studentId={studentId}
          track={trackCommitted}
          initialRationale={trackRationale}
          onRationaleSaved={onRationaleSaved}
          onContinue={onContinue}
        />
      )}

      {/* ── Data Science / AI fork — a sub-flavor of scientific inquiry ──── */}
      {trackCommitted === 'scientific_method' && (
        <Box sx={{ mt: 3 }}>
          <StudyModeFork projectId={projectId} studentId={studentId} phase="explore" />
        </Box>
      )}
    </Box>
  )
}

interface AlignmentResult {
  alignment:   'aligned' | 'partial' | 'misaligned'
  feedback:    string
  suggestions: string[]
}

function parseRationale(stored: string | null): { framing: string; resolution: string } {
  if (!stored) return { framing: '', resolution: '' }
  try {
    const parsed = JSON.parse(stored)
    if (typeof parsed?.framing === 'string' && typeof parsed?.resolution === 'string') {
      return { framing: parsed.framing, resolution: parsed.resolution }
    }
  } catch {
    /* fall through */
  }
  // Legacy free-text rationale — drop it into framing so the student can split it.
  return { framing: stored, resolution: '' }
}

function JustifyPanel({
  projectId, studentId, track, initialRationale, onRationaleSaved, onContinue,
}: {
  projectId:        string
  studentId:        string
  track:            TrackKind
  initialRationale: string | null
  onRationaleSaved: (rationale: string) => void
  onContinue:       () => void
}) {
  const isEng   = track === 'engineering_design'
  const seeded  = parseRationale(initialRationale)
  const initialHasSaved = !!(seeded.framing && seeded.resolution)
  const [framing,    setFraming]    = useState(seeded.framing)
  const [resolution, setResolution] = useState(seeded.resolution)
  const [editing,    setEditing]    = useState(!initialHasSaved)
  const [eval_,      setEval]       = useState<AlignmentResult | null>(null)
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  // Seed when the parent's rationale arrives after first render. Switch to
  // the saved-recap view rather than the form if data shows up.
  useEffect(() => {
    if (initialRationale && !framing && !resolution) {
      const next = parseRationale(initialRationale)
      if (next.framing || next.resolution) {
        setFraming(next.framing)
        setResolution(next.resolution)
        setEditing(false)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRationale])

  const minChars     = 30
  const framingT     = framing.trim()
  const resolutionT  = resolution.trim()
  const framingShort = framingT.length < minChars
  const resoShort    = resolutionT.length < minChars
  const blocked      = framingShort || resoShort

  const framingLabel    = isEng ? 'The problem'        : 'The question you want to ask'
  const resolutionLabel = isEng ? 'A possible solution' : 'The answer you expect'
  const framingPrompt   = isEng
    ? 'Who has this problem? In what context does it happen?'
    : 'What relationship or comparison do you want to investigate?'
  const resolutionPrompt = isEng
    ? 'What would you build to solve it, and how does that fix the problem?'
    : 'What do you predict you\'ll find? Why would that be the answer?'
  const framingPlaceholder = isEng
    ? 'Shelter dogs eat slowly when they\'re stressed during meal time, which makes feeding sessions take longer and stresses staff out.'
    : 'Does the music played in the room change how fast shelter dogs eat their meals?'
  const resoPlaceholder = isEng
    ? 'A speaker by the bowl that plays calming music when a dog approaches — quieter ambient music lowers their stress, so they relax and finish their food sooner.'
    : 'Calm music will help dogs eat faster than no music — calming sounds reduce stress, so they\'ll be more relaxed and willing to eat.'

  async function submit() {
    if (loading || blocked) return
    setLoading(true); setError(null); setEval(null)
    try {
      const stored = JSON.stringify({ framing: framingT, resolution: resolutionT })
      const { error: saveErr } = await supabase
        .from('research_question')
        .update({ track_rationale: stored, updated_at: new Date().toISOString() })
        .eq('project_id', projectId)
      if (saveErr) throw new Error(saveErr.message)
      onRationaleSaved(stored)

      // Send a structured statement so the AI evaluates each part by name.
      const aiStatement = isEng
        ? `Problem: ${framingT}\n\nSolution: ${resolutionT}`
        : `Question: ${framingT}\n\nAnswer I expect: ${resolutionT}`

      const { data, error: invokeErr } = await supabase.functions.invoke('step2-evaluate-track-commitment', {
        body: { project_id: projectId, student_id: studentId, track, statement: aiStatement },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      setEval(data as AlignmentResult)
      setEditing(false)
    } catch (e: any) {
      setError(e.message ?? 'Could not get feedback — your statement is saved; try again or continue.')
    } finally {
      setLoading(false)
    }
  }

  function revise() {
    setEditing(true); setEval(null); setError(null)
  }

  const heading = isEng ? 'Justify your research type: problem → solution' : 'Justify your research type: question → answer'
  const helper  = isEng
    ? 'Name the problem and a possible solution you might build. Rough is fine — you\'ll refine in 2.2.'
    : 'Write the question and the answer you expect to find. Rough is fine — you\'ll refine in 2.2.'

  const alignmentColor = eval_?.alignment === 'aligned'
    ? 'success'
    : eval_?.alignment === 'partial'
      ? 'warning'
      : 'error'
  const alignmentLabel = eval_?.alignment === 'aligned'
    ? 'Looks aligned'
    : eval_?.alignment === 'partial'
      ? 'Generally aligned'
      : 'Drifts to the other path'

  return (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
          {heading}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
          {helper}
        </Typography>

        {editing ? (
          <>
            <TextField
              fullWidth size="small" multiline rows={3}
              label={framingLabel}
              value={framing}
              onChange={e => setFraming(e.target.value)}
              placeholder={framingPlaceholder}
              disabled={loading}
              helperText={
                framingShort
                  ? `${framingT.length} / ${minChars} min — ${framingPrompt}`
                  : framingPrompt
              }
              error={framingT.length > 0 && framingShort}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth size="small" multiline rows={3}
              label={resolutionLabel}
              value={resolution}
              onChange={e => setResolution(e.target.value)}
              placeholder={resoPlaceholder}
              disabled={loading}
              helperText={
                resoShort
                  ? `${resolutionT.length} / ${minChars} min — ${resolutionPrompt}`
                  : resolutionPrompt
              }
              error={resolutionT.length > 0 && resoShort}
              sx={{ mb: 2 }}
            />
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="contained" onClick={submit} disabled={loading || blocked}>
                {loading
                  ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Reviewing…</>
                  : 'Submit for feedback'}
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                p: 1.5, borderRadius: 1.5, mb: 2,
                bgcolor: 'rgba(15,43,76,0.03)', border: '1px solid', borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                {framingLabel}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55, mb: 1 }}>
                {framingT}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                {resolutionLabel}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>
                {resolutionT}
              </Typography>
            </Box>

            {eval_ && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip size="small" color={alignmentColor} label={alignmentLabel} />
                </Box>
                <Typography variant="body2" sx={{ mb: eval_.suggestions.length ? 1.5 : 2, lineHeight: 1.6 }}>
                  {eval_.feedback}
                </Typography>
                {eval_.suggestions.length > 0 && (
                  <Box component="ul" sx={{ pl: 2.25, m: 0, mb: 2 }}>
                    {eval_.suggestions.map((s, i) => (
                      <li key={i}>
                        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                          {s}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontStyle: 'italic' }}>
                  The AI is advisory — your selection stands either way. Refine if it helps; otherwise continue and keep working in 2.2.
                </Typography>
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="text" onClick={revise}>Revise</Button>
              <Button variant="contained" onClick={onContinue}>
                Continue to {isEng ? '2.2 Problem Statement' : '2.2 Research Question'}
              </Button>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface ExampleItem {
  title:   string
  summary: string
}

function CriteriaColumn({
  type, heading, bullets, projectId, studentId, topicAvailable,
}: {
  type:           TrackKind
  heading:        string
  bullets:        string[]
  projectId:      string
  studentId:      string
  topicAvailable: boolean
}) {
  const [examples, setExamples] = useState<ExampleItem[] | null>(null)
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  async function generate() {
    setLoading(true); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step2-research-type-examples', {
        body: { project_id: projectId, student_id: studentId, type },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      const got = (data?.examples ?? []) as ExampleItem[]
      if (!got.length) throw new Error('No examples returned.')
      setExamples(got)
    } catch (e: any) {
      setError(e.message ?? 'Could not generate examples — please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card variant="outlined" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {heading}
        </Typography>
        <Box component="ul" sx={{ pl: 2.25, m: 0, mb: 2, '& li': { mb: 0.5 } }}>
          {bullets.map((b, i) => (
            <li key={i}>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                {b}
              </Typography>
            </li>
          ))}
        </Box>

        <Box sx={{ mt: 'auto' }}>
          <Button
            size="small"
            variant={examples ? 'text' : 'outlined'}
            onClick={generate}
            disabled={loading || !topicAvailable}
            sx={{ alignSelf: 'flex-start' }}
          >
            {loading
              ? <><CircularProgress size={14} sx={{ mr: 1 }} />Generating…</>
              : examples ? 'Regenerate examples' : 'Give me 2 examples'}
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}

        {examples && (
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {examples.map((ex, i) => (
              <Box
                key={i}
                sx={{
                  p:           1.5,
                  borderRadius: 1.5,
                  bgcolor:     'rgba(15,43,76,0.03)',
                  border:      '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5, lineHeight: 1.4 }}>
                  {ex.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {ex.summary}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

function TrackPickCard({
  track: _track, title, summary, selected, loading, disabled, onClick,
}: {
  track:    TrackKind
  title:    string
  summary:  string
  selected: boolean
  loading:  boolean
  disabled: boolean
  onClick:  () => void
}) {
  return (
    <Card
      variant="outlined"
      sx={{
        flex:        1,
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        bgcolor:     selected ? 'rgba(15,43,76,0.04)' : 'background.paper',
        transition:  'border-color 0.15s, background-color 0.15s',
      }}
    >
      <CardActionArea onClick={onClick} disabled={disabled}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            {selected && <Chip size="small" label="Selected" color="primary" />}
            {loading && <CircularProgress size={16} />}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {summary}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function QuestionOrProblemTab({
  track, topic, topicDesc, trackRationale,
}: {
  track:          TrackKind | null
  topic:          string | null
  topicDesc:      string | null
  trackRationale: string | null
}) {
  const isEng = track === 'engineering_design'
  const label = isEng ? '2.2 Problem Statement' : '2.2 Research Question'
  const placeholderBody = isEng
    ? 'Coming next: sharpen your problem statement — who is affected, what context, what constraints — then specify what your built solution must do.'
    : 'Coming next: AI proposes candidate research questions, you accept/reject with your reasoning. Pick a finalist to lock in.'

  const { framing, resolution } = parseRationale(trackRationale)
  const hasStarting = framing.trim().length > 0 || resolution.trim().length > 0
  const framingLabel    = isEng ? 'The problem'         : 'The question you want to ask'
  const resolutionLabel = isEng ? 'A possible solution' : 'The answer you expect'

  return (
    <Box sx={{ maxWidth: 760, pt: 2 }}>
      {/* ── Topic recap (same shape as 2.1) ───────────────────────────── */}
      {topic && (
        <Card variant="outlined" sx={{ mb: 2, bgcolor: 'rgba(15,43,76,0.03)' }}>
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block', lineHeight: 1.4 }}
            >
              Your research topic
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, mt: 0.25 }}>
              {topic}
            </Typography>
            {topicDesc && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.6 }}>
                {topicDesc}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Starting point from 2.1 ────────────────────────────────────── */}
      {hasStarting && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: '0.08em', fontSize: '0.7rem', display: 'block', lineHeight: 1.4, mb: 0.5 }}
            >
              Your starting point — from 2.1
            </Typography>
            {framing.trim() && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>
                  {framingLabel}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {framing}
                </Typography>
              </>
            )}
            {resolution.trim() && (
              <>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1.5 }}>
                  {resolutionLabel}
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {resolution}
                </Typography>
              </>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, fontStyle: 'italic' }}>
              You'll sharpen this in {label}. Your wording above is a starting point, not the final answer.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Placeholder title={label} body={placeholderBody} />
    </Box>
  )
}


function MentorReviewTab({ projectId, status, mentorNote }: {
  projectId:  string
  status:     DraftStatus
  mentorNote: string | null
}) {
  const isPending = status === 'submitted'
  const isApproved = status === 'approved_draft' || status === 'completed'
  const hasNote   = !!mentorNote && (status === 'draft' || isApproved)

  const headline =
    status === 'approved_draft' ? 'Draft approved — Step 3 unlocks' :
    status === 'completed'      ? 'Approved' :
    status === 'submitted'      ? 'Awaiting mentor review' :
    hasNote                     ? 'Sent back with comment' :
                                  'Not yet submitted'

  return (
    <Box sx={{ maxWidth: 760, py: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Mentor Review</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Track your mentor's review of your Step 2 draft and exchange messages while it's pending.
      </Typography>

      {/* Status card */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Status
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{headline}</Typography>
        </CardContent>
      </Card>

      {/* Latest mentor note — send-back or approval */}
      {hasNote && (
        <Card variant="outlined" sx={{ mb: 3, borderColor: isApproved ? 'success.light' : 'warning.light' }}>
          <CardContent>
            <Typography variant="caption" color={isApproved ? 'success.main' : 'warning.main'} sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Mentor's note
            </Typography>
            <Box sx={{ mt: 0.75 }}><Markdown>{mentorNote}</Markdown></Box>
          </CardContent>
        </Card>
      )}

      {/* Conversation thread — editable while pending */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Conversation with your mentor
          </Typography>
          <ConversationThread
            projectId={projectId}
            editable={isPending}
            viewerSide="student"
          />
        </CardContent>
      </Card>
    </Box>
  )
}

// Shared submit bar. General rule: a mentor viewing a student (View-As) sees
// the same submit button the student sees, but disabled with a hint — so they
// can tell the control exists and whether the student has submitted.
function SubmitBar({ isReadOnly, disabled, submitting, isReturning, onSubmit }: {
  isReadOnly: boolean; disabled: boolean; submitting: boolean; isReturning: boolean; onSubmit: () => void
}) {
  const { viewerIsStudent } = useViewerStudent()
  if (isReadOnly) return null
  return (
    <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
      <Button
        variant="contained"
        disabled={disabled || submitting || !viewerIsStudent}
        onClick={onSubmit}
      >
        {submitting
          ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Submitting…</>
          : isReturning ? 'Resubmit draft for mentor review' : 'Submit draft for mentor review'}
      </Button>
      {!viewerIsStudent && (
        <Typography variant="caption" color="text.secondary">
          Only the student can submit — you're viewing as them.
        </Typography>
      )}
    </Box>
  )
}

function Placeholder({ title, body }: { title: string; body: string }) {
  return (
    <Box sx={{ maxWidth: 720, py: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
        {body}
      </Typography>
      <Card variant="outlined" sx={{ bgcolor: 'rgba(15,43,76,0.03)' }}>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            This panel is a placeholder while we build Step 2 piece by piece.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  )
}

// ── Method approach: what the student will build to collect data ─────────────
// Tailored per track. Free-text "Other" lives in the details box below the chips.

const METHOD_CATEGORIES_SCI = [
  'Build a device or apparatus',
  'Develop software / an AI model',
  'Analyze a public dataset (code)',
  'Sensors / data loggers',
  'Manual observation & scoring',
  'Smartphone app / off-the-shelf tool',
]

const METHOD_CATEGORIES_ENG = [
  'Build a physical prototype / device',
  'Develop software / an AI model',
  'Microcontroller (Arduino / Raspberry Pi)',
  '3D print / fabricate parts',
  'Write code / app',
  'Sensors / actuators',
  'Off-the-shelf hardware assembly',
  'Simulation / modeling',
]

function MethodCategoryPicker({
  options, selected, onChange, disabled,
}: {
  options: string[]
  selected: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}) {
  function toggle(opt: string) {
    if (disabled) return
    onChange(selected.includes(opt) ? selected.filter(s => s !== opt) : [...selected, opt])
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
      {options.map(opt => {
        const on = selected.includes(opt)
        return (
          <Chip
            key={opt}
            label={opt}
            onClick={() => toggle(opt)}
            color={on ? 'primary' : 'default'}
            variant={on ? 'filled' : 'outlined'}
            disabled={disabled}
            sx={{ cursor: disabled ? 'default' : 'pointer' }}
          />
        )
      })}
    </Box>
  )
}

// ── 2.2s Research Question (Scientific Method) ───────────────────────────────

function ResearchQuestionTabSci({
  projectId, studentId, topic, topicDesc, trackRationale,
  draftPayload, status, mentorNote,
  onChange, onSubmitted,
}: {
  projectId:      string
  studentId:      string
  topic:          string | null
  topicDesc:      string | null
  trackRationale: string | null
  draftPayload:   DraftPayload | null
  status:         DraftStatus
  mentorNote:     string | null
  onChange:       (next: DraftPayload) => void
  onSubmitted:    () => void
}) {
  const seeded = draftPayload ?? {}
  const [iv,         setIv]        = useState<string>(seeded.iv ?? '')
  const [dv,         setDv]        = useState<string>(seeded.dv ?? '')
  const [cv,         setCv]        = useState<string[]>(seeded.cv && seeded.cv.length > 0 ? seeded.cv : [''])
  const [question,   setQuestion]  = useState<string>(seeded.question ?? '')
  const [methodCats, setMethodCats]= useState<string[]>(seeded.method_categories ?? [])
  const [method,     setMethod]    = useState<string>(seeded.method ?? '')
  const [feedback,   setFeedback]  = useState<DraftPayload['ai_feedback']>(seeded.ai_feedback ?? {})
  const [staleSet,   setStaleSet]  = useState<Set<string>>(new Set())
  const [evaluating, setEvaluating]= useState<string | null>(null)
  const [submitting, setSubmitting]= useState(false)
  const [error,      setError]     = useState<string | null>(null)
  const [softGate,   setSoftGate]  = useState<{ thinking: string[]; caution: string[] } | null>(null)
  /* Collapsed by default. This card is orientation — it says so itself — and
     open it filled the first screen of a page whose whole job is the research
     question, pushing the question below the fold on arrival. */
  const [contextOpen, setContextOpen] = useState(false)

  const isReadOnly  = status === 'submitted' || status === 'approved_draft' || status === 'completed'
  const isReturning = status === 'draft' && !!mentorNote
  const isSubmitted = status === 'submitted'
  const isApproved  = status === 'approved_draft' || status === 'completed'

  const { framing: rougheQ, resolution: roughA } = parseRationale(trackRationale)

  function payload(): DraftPayload {
    return {
      iv, dv, cv: cv.filter(s => s.trim()), question,
      method_categories: methodCats, method,
      ai_feedback: feedback,
    }
  }

  function markStale(field: 'iv' | 'dv' | 'cv' | 'question' | 'method') {
    setStaleSet(prev => {
      if (prev.has(field)) return prev
      const next = new Set(prev); next.add(field); return next
    })
  }

  async function evaluate(field: 'iv' | 'dv' | 'cv' | 'question' | 'method') {
    if (evaluating) return
    setEvaluating(field); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step2-evaluate-draft', {
        body: { project_id: projectId, student_id: studentId, field, payload: payload() },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      const nextFeedback = { ...(feedback ?? {}), [field]: data.evaluation as AIEvaluation }
      setFeedback(nextFeedback)
      setStaleSet(prev => {
        if (!prev.has(field)) return prev
        const next = new Set(prev); next.delete(field); return next
      })
      // Persist progress so AI feedback isn't lost on navigation.
      await supabase.from('research_question').update({
        draft_payload: { ...payload(), ai_feedback: nextFeedback },
        updated_at:    new Date().toISOString(),
      }).eq('project_id', projectId)
      onChange({ ...payload(), ai_feedback: nextFeedback })
    } catch (e: any) {
      setError(e.message ?? 'Could not get AI feedback — please try again.')
    } finally {
      setEvaluating(null)
    }
  }

  function readyToSubmit(): boolean {
    return iv.trim().length >= 3
        && dv.trim().length >= 3
        && cv.some(s => s.trim().length >= 3)
        && question.trim().length >= 10
        && (methodCats.length > 0 || method.trim().length >= 10)
  }

  function flagsByLevel(): { thinking: string[]; caution: string[] } {
    const labels: Record<string, string> = {
      iv: 'Independent variable', dv: 'Dependent variable', cv: 'Controlled variables',
      question: 'Research question', method: 'Method approach',
    }
    const thinking: string[] = []
    const caution:  string[] = []
    for (const [key, val] of Object.entries(feedback ?? {})) {
      const v = val as AIEvaluation | undefined
      const states = [v?.shape?.state, v?.anchor?.state]
      if (states.includes('thinking')) thinking.push(labels[key] ?? key)
      else if (states.includes('caution')) caution.push(labels[key] ?? key)
    }
    return { thinking, caution }
  }

  async function submit(force = false) {
    if (submitting || !readyToSubmit()) return
    if (!force) {
      const flags = flagsByLevel()
      if (flags.thinking.length > 0) { setSoftGate(flags); return }
    }
    setSubmitting(true); setError(null); setSoftGate(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step2-submit-draft', {
        body: { project_id: projectId, draft_payload: payload() },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      onSubmitted()
    } catch (e: any) {
      setError(e.message ?? 'Could not submit — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  // ── Renders ────────────────────────────────────────────────────────────
  return (
    <Box sx={{ maxWidth: 800, pt: 2, pb: 6 }}>
      {/* Status banners */}
      {isReturning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Your mentor sent this back:</strong>
          {mentorNote && <Box sx={{ mt: 0.5 }}><Markdown>{mentorNote}</Markdown></Box>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Refine your draft and resubmit when ready.
          </Typography>
        </Alert>
      )}
      {isSubmitted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Submitted to your mentor for review.</strong>{' '}
          You'll see their decision on your dashboard. They'll either approve your draft and unlock Step 3, or send it back with feedback.
        </Alert>
      )}
      {isApproved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Draft approved by your mentor.</strong>{' '}
          Step 3 (Background Research) is unlocked. You'll come back here to refine the draft into a final question once you've researched the literature.
        </Alert>
      )}

      {/* APPROVED ONLY. Once the research question is settled it is the thing
          a student comes back to this page to re-read, so it belongs at the
          top. While they are still working it out, showing it up here would
          hand them the answer before the work that produces it — the page
          teaches variables first, then the question formed from them. So the
          card is deliberately absent until approval, and is a read-only
          window: isApproved already implies isReadOnly. */}
      {isApproved && (
        <Box
          id="rq-top"
          sx={{
            mb: 3, p: 2, borderRadius: 2,
            border: '1px solid', borderColor: 'primary.light',
            bgcolor: 'rgba(15,43,76,0.02)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
              Your research question
            </Typography>
            <Button
              size="small"
              onClick={() => document.getElementById('rq-editor')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              sx={{ flexShrink: 0 }}
            >
              See the full draft
            </Button>
          </Box>
          <Typography sx={{ fontSize: 16, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {question}
          </Typography>
        </Box>
      )}

      {/* Context — collapsible card */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'rgba(15,43,76,0.03)' }}>
        <CardActionArea onClick={() => setContextOpen(o => !o)}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Context — your topic and 2.1 commitments</Typography>
              <Typography variant="caption" color="text.secondary">
                Orientation only. Not the focus.
              </Typography>
            </Box>
            <ExpandMoreIcon
              sx={{ transform: contextOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'text.secondary' }}
            />
          </Box>
        </CardActionArea>
        <Collapse in={contextOpen} unmountOnExit>
          <CardContent sx={{ pt: 0 }}>
            {topic && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em', fontSize: '0.7rem' }}>Your research topic</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{topic}</Typography>
                {topicDesc && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{topicDesc}</Typography>}
              </Box>
            )}
            {(rougheQ || roughA) && (
              <>
                {rougheQ && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>Your rough question (from 2.1)</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{rougheQ}</Typography>
                  </>
                )}
                {roughA && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>The answer you expect (from 2.1)</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{roughA}</Typography>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* ── Define your variables ─────────────────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Define your variables</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        Sketch what you're thinking about. You can list more than one candidate in each box —
        broad ideas are fine.
      </Typography>

      <DraftField
        label="Independent variable(s)"
        definition="The factor you change between conditions to see if it makes a difference."
        helperText="e.g., type of music, volume, time of day. List one or several."
        value={iv}
        onChange={v => { setIv(v); markStale('iv') }}
        feedback={feedback?.iv}
        stale={staleSet.has('iv')}
        evaluating={evaluating === 'iv'}
        onEvaluate={() => evaluate('iv')}
        disabled={isReadOnly}
        rows={4}
      />

      <DraftField
        label="Dependent variable(s)"
        definition="The outcome you observe or measure to see if the independent variable had an effect."
        helperText="e.g., feeding speed, vocalizations, restlessness. List one or several."
        value={dv}
        onChange={v => { setDv(v); markStale('dv') }}
        feedback={feedback?.dv}
        stale={staleSet.has('dv')}
        evaluating={evaluating === 'dv'}
        onEvaluate={() => evaluate('dv')}
        disabled={isReadOnly}
        rows={4}
      />

      <ListField
        title="Controlled variables"
        definition="Factors you keep the same across conditions so they don't muddy the experiment."
        exampleHint="e.g., dog age range, room temperature, feeding amount."
        placeholder="e.g., dog age range"
        items={cv}
        onChange={next => { setCv(next); markStale('cv') }}
        feedback={feedback?.cv}
        stale={staleSet.has('cv')}
        evaluating={evaluating === 'cv'}
        onEvaluate={() => evaluate('cv')}
        disabled={isReadOnly}
      />

      <Divider sx={{ my: 3 }} />

      {/* ── Draft your research question ──────────────────────────────── */}
      <Typography id="rq-editor" variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Draft your research question</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        Express the relationship you want to investigate. The shape that works:{' '}
        <em>"How does [what changes] affect [what you measure]?"</em>
      </Typography>
      <DraftField
        label="Draft research question"
        helperText="A relationship-shaped question linking your variables."
        value={question}
        onChange={v => { setQuestion(v); markStale('question') }}
        feedback={feedback?.question}
        stale={staleSet.has('question')}
        evaluating={evaluating === 'question'}
        onEvaluate={() => evaluate('question')}
        disabled={isReadOnly}
        rows={4}
      />

      <Divider sx={{ my: 3 }} />

      {/* ── How will you investigate this? ────────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>How will you investigate this?</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
        What will you build or use to collect your data? Pick any that apply — you can pick more
        than one (e.g. a device <em>and</em> software).
      </Typography>
      <MethodCategoryPicker
        options={METHOD_CATEGORIES_SCI}
        selected={methodCats}
        onChange={next => { setMethodCats(next); markStale('method') }}
        disabled={isReadOnly}
      />
      <DraftField
        label="Details (optional)"
        helperText="Add specifics if you have them — e.g. 'smartphone video + Python/AI to score behaviors'."
        value={method}
        onChange={v => { setMethod(v); markStale('method') }}
        feedback={feedback?.method}
        stale={staleSet.has('method')}
        evaluating={evaluating === 'method'}
        onEvaluate={() => evaluate('method')}
        disabled={isReadOnly}
        rows={4}
      />

      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

      {/* ── Submit ─────────────────────────────────────────────────────── */}
      <SubmitBar
        isReadOnly={isReadOnly}
        disabled={!readyToSubmit()}
        submitting={submitting}
        isReturning={isReturning}
        onSubmit={() => submit(false)}
      />

      {/* Soft-gate dialog */}
      <Dialog open={!!softGate} onClose={() => setSoftGate(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit anyway?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            AI flagged a few things to think about on:
          </Typography>
          {softGate?.thinking && softGate.thinking.length > 0 && (
            <Box component="ul" sx={{ pl: 2.25, m: 0, mb: 1.5 }}>
              {softGate.thinking.map(label => (
                <li key={label}><Typography variant="body2">{label}</Typography></li>
              ))}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            You can submit anyway — your mentor will review your work regardless. Or refine first
            if you want to address these.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSoftGate(null)} disabled={submitting}>Refine first</Button>
          <Button variant="contained" onClick={() => submit(true)} disabled={submitting}>
            {submitting
              ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Submitting…</>
              : 'Submit anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ── Small subcomponents ──────────────────────────────────────────────────────

function DraftField({
  label, definition, helperText, value, onChange,
  feedback, stale, evaluating, onEvaluate, disabled, rows = 4,
}: {
  label:        string
  definition?:  string
  helperText:   string
  value:        string
  onChange:     (v: string) => void
  feedback:     AIEvaluation | undefined
  stale:        boolean
  evaluating:   boolean
  onEvaluate:   () => void
  disabled:     boolean
  rows?:        number
}) {
  const tooShort = value.trim().length < 3
  return (
    <Box sx={{ mb: 2.5 }}>
      {definition && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic', lineHeight: 1.5 }}>
          {definition}
        </Typography>
      )}
      <TextField
        fullWidth multiline minRows={rows} size="small"
        label={label}
        value={value}
        onChange={e => onChange(e.target.value)}
        helperText={helperText}
        disabled={disabled}
        sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: '0.875rem', lineHeight: 1.5 } }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onEvaluate}
          disabled={evaluating || disabled || tooShort}
        >
          {evaluating
            ? <><CircularProgress size={14} sx={{ mr: 1 }} />Evaluating…</>
            : feedback ? 'Refresh AI feedback' : 'Get AI feedback'}
        </Button>
        {stale && feedback && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Feedback is stale — re-check?
          </Typography>
        )}
      </Box>
      {feedback && <FeedbackBlock feedback={feedback} stale={stale} />}
    </Box>
  )
}

function ListField({
  title, definition, exampleHint, placeholder,
  items, onChange, feedback, stale, evaluating, onEvaluate, disabled,
}: {
  title:       string
  definition:  string
  exampleHint: string
  placeholder: string
  items:       string[]
  onChange:    (next: string[]) => void
  feedback:    AIEvaluation | undefined
  stale:       boolean
  evaluating:  boolean
  onEvaluate:  () => void
  disabled:    boolean
}) {
  function update(i: number, v: string) { onChange(items.map((it, idx) => idx === i ? v : it)) }
  function add()    { onChange([...items, '']) }
  function remove(i: number) { onChange(items.filter((_, idx) => idx !== i)) }

  const nonEmpty = items.filter(s => s.trim()).length

  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontStyle: 'italic', lineHeight: 1.5 }}>
        {definition}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {exampleHint}
      </Typography>
      {items.map((it, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
          <TextField
            fullWidth size="small" multiline minRows={2}
            placeholder={placeholder}
            value={it}
            onChange={e => update(i, e.target.value)}
            disabled={disabled}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', lineHeight: 1.5 } }}
          />
          {!disabled && items.length > 1 && (
            <IconButton size="small" onClick={() => remove(i)} sx={{ mt: 0.25, color: 'text.secondary' }}>
              <CancelIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ))}
      {!disabled && (
        <Button size="small" variant="text" onClick={add} sx={{ mb: 1 }}>+ Add another</Button>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={onEvaluate}
          disabled={evaluating || disabled || nonEmpty === 0}
        >
          {evaluating
            ? <><CircularProgress size={14} sx={{ mr: 1 }} />Evaluating…</>
            : feedback ? 'Refresh AI feedback' : 'Get AI feedback'}
        </Button>
        {stale && feedback && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Feedback is stale — re-check?
          </Typography>
        )}
      </Box>
      {feedback && <FeedbackBlock feedback={feedback} stale={stale} />}
    </Box>
  )
}

function FeedbackBlock({ feedback, stale }: { feedback: AIEvaluation; stale: boolean }) {
  const dims: Array<{ key: 'shape' | 'anchor'; label: string }> = [
    { key: 'shape',  label: 'Shape' },
    { key: 'anchor', label: 'Anchor' },
  ]
  const stateColor = (s: string) =>
    s === 'ok' ? 'success.main' : s === 'caution' ? 'warning.main' : 'error.main'
  const stateBg = (s: string) =>
    s === 'ok' ? 'rgba(46,125,50,0.08)' : s === 'caution' ? 'rgba(237,108,2,0.08)' : 'rgba(198,40,40,0.08)'
  const stateGlyph = (s: string) =>
    s === 'ok' ? '✓' : s === 'caution' ? '⚠' : '?'

  return (
    <Box sx={{ mt: 1.5, opacity: stale ? 0.55 : 1 }}>
      {dims.map(d => {
        const dim = feedback[d.key]
        if (!dim) return null
        return (
          <Box key={d.key} sx={{
            display: 'flex', gap: 1, p: 1.25, mb: 0.75, borderRadius: 1,
            bgcolor: stateBg(dim.state),
            border: '1px solid', borderColor: `${stateColor(dim.state)}40`,
          }}>
            <Typography sx={{ color: stateColor(dim.state), fontWeight: 700, minWidth: 18, textAlign: 'center' }}>
              {stateGlyph(dim.state)}
            </Typography>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: stateColor(dim.state), display: 'block' }}>
                {d.label}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                {dim.note}
              </Typography>
            </Box>
          </Box>
        )
      })}
      {feedback.ai_disclaimer && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
          ⓘ {feedback.ai_disclaimer}
        </Typography>
      )}
    </Box>
  )
}

// ── 2.2e Problem Statement (Engineering Design) ──────────────────────────────

function ProblemStatementTabEng({
  projectId, studentId, topic, topicDesc, trackRationale,
  draftPayload, status, mentorNote,
  onChange, onSubmitted,
}: {
  projectId:      string
  studentId:      string
  topic:          string | null
  topicDesc:      string | null
  trackRationale: string | null
  draftPayload:   DraftPayload | null
  status:         DraftStatus
  mentorNote:     string | null
  onChange:       (next: DraftPayload) => void
  onSubmitted:    () => void
}) {
  const seeded = draftPayload ?? {}
  const [userCtx,    setUserCtx]   = useState<string>(seeded.user_context ?? '')
  const [need,       setNeed]      = useState<string>(seeded.need ?? '')
  const [constraints, setConstraints] = useState<string[]>(seeded.constraints && seeded.constraints.length > 0 ? seeded.constraints : [''])
  const [success,    setSuccess]   = useState<string[]>(seeded.success_criteria && seeded.success_criteria.length > 0 ? seeded.success_criteria : [''])
  const [statement,  setStatement] = useState<string>(seeded.problem_statement ?? '')
  const [methodCats, setMethodCats]= useState<string[]>(seeded.method_categories ?? [])
  const [method,     setMethod]    = useState<string>(seeded.method ?? '')
  const [feedback,   setFeedback]  = useState<DraftPayload['ai_feedback']>(seeded.ai_feedback ?? {})
  const [staleSet,   setStaleSet]  = useState<Set<string>>(new Set())
  const [evaluating, setEvaluating]= useState<string | null>(null)
  const [submitting, setSubmitting]= useState(false)
  const [error,      setError]     = useState<string | null>(null)
  const [softGate,   setSoftGate]  = useState<{ thinking: string[]; caution: string[] } | null>(null)
  /* Collapsed by default. This card is orientation — it says so itself — and
     open it filled the first screen of a page whose whole job is the research
     question, pushing the question below the fold on arrival. */
  const [contextOpen, setContextOpen] = useState(false)

  const isReadOnly  = status === 'submitted' || status === 'approved_draft' || status === 'completed'
  const isReturning = status === 'draft' && !!mentorNote
  const isSubmitted = status === 'submitted'
  const isApproved  = status === 'approved_draft' || status === 'completed'

  const { framing: roughProblem, resolution: roughSolution } = parseRationale(trackRationale)

  function payload(): DraftPayload {
    return {
      user_context:      userCtx,
      need,
      constraints:       constraints.filter(s => s.trim()),
      success_criteria:  success.filter(s => s.trim()),
      problem_statement: statement,
      method_categories: methodCats,
      method,
      ai_feedback:       feedback,
    }
  }

  function markStale(field: DraftField) {
    setStaleSet(prev => {
      if (prev.has(field)) return prev
      const next = new Set(prev); next.add(field); return next
    })
  }

  async function evaluate(field: DraftField) {
    if (evaluating) return
    setEvaluating(field); setError(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step2-evaluate-draft', {
        body: { project_id: projectId, student_id: studentId, field, payload: payload() },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      const nextFeedback = { ...(feedback ?? {}), [field]: data.evaluation as AIEvaluation }
      setFeedback(nextFeedback)
      setStaleSet(prev => {
        if (!prev.has(field)) return prev
        const next = new Set(prev); next.delete(field); return next
      })
      await supabase.from('research_question').update({
        draft_payload: { ...payload(), ai_feedback: nextFeedback },
        updated_at:    new Date().toISOString(),
      }).eq('project_id', projectId)
      onChange({ ...payload(), ai_feedback: nextFeedback })
    } catch (e: any) {
      setError(e.message ?? 'Could not get AI feedback — please try again.')
    } finally {
      setEvaluating(null)
    }
  }

  function readyToSubmit(): boolean {
    return userCtx.trim().length >= 3
        && need.trim().length >= 3
        && constraints.some(s => s.trim().length >= 3)
        && success.some(s => s.trim().length >= 3)
        && statement.trim().length >= 10
        && (methodCats.length > 0 || method.trim().length >= 10)
  }

  function flagsByLevel(): { thinking: string[]; caution: string[] } {
    const labels: Record<string, string> = {
      user_context:     'User + context',
      need:             'Need / pain',
      constraints:      'Constraints',
      success_criteria: 'Success criteria',
      problem_statement:'Problem statement',
      method:           'Method approach',
    }
    const thinking: string[] = []
    const caution:  string[] = []
    for (const [key, val] of Object.entries(feedback ?? {})) {
      const v = val as AIEvaluation | undefined
      const states = [v?.shape?.state, v?.anchor?.state]
      if (states.includes('thinking')) thinking.push(labels[key] ?? key)
      else if (states.includes('caution')) caution.push(labels[key] ?? key)
    }
    return { thinking, caution }
  }

  async function submit(force = false) {
    if (submitting || !readyToSubmit()) return
    if (!force) {
      const flags = flagsByLevel()
      if (flags.thinking.length > 0) { setSoftGate(flags); return }
    }
    setSubmitting(true); setError(null); setSoftGate(null)
    try {
      const { data, error: invokeErr } = await supabase.functions.invoke('step2-submit-draft', {
        body: { project_id: projectId, draft_payload: payload() },
      })
      if (invokeErr) throw new Error(invokeErr.message)
      if (data?.error) throw new Error(data.error)
      onSubmitted()
    } catch (e: any) {
      setError(e.message ?? 'Could not submit — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 800, pt: 2, pb: 6 }}>
      {/* Status banners */}
      {isReturning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Your mentor sent this back:</strong>
          {mentorNote && <Box sx={{ mt: 0.5 }}><Markdown>{mentorNote}</Markdown></Box>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Refine your draft and resubmit when ready.
          </Typography>
        </Alert>
      )}
      {isSubmitted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Submitted to your mentor for review.</strong>{' '}
          You'll see their decision on your dashboard. They'll either approve your draft and unlock Step 3, or send it back with feedback.
        </Alert>
      )}
      {isApproved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Draft approved by your mentor.</strong>{' '}
          Step 3 (Background Research) is unlocked. You'll come back here to refine the draft into a final problem statement once you've researched the literature.
        </Alert>
      )}

      {/* APPROVED ONLY. Once the problem statement is settled it is the thing
          a student comes back to this page to re-read, so it belongs at the
          top. While they are still working it out, showing it up here would
          hand them the answer before the work that produces it — the page
          teaches variables first, then the question formed from them. So the
          card is deliberately absent until approval, and is a read-only
          window: isApproved already implies isReadOnly. */}
      {isApproved && (
        <Box
          id="ps-top"
          sx={{
            mb: 3, p: 2, borderRadius: 2,
            border: '1px solid', borderColor: 'primary.light',
            bgcolor: 'rgba(15,43,76,0.02)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, mb: 1 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: '0.08em', color: 'text.secondary' }}>
              Your problem statement
            </Typography>
            <Button
              size="small"
              onClick={() => document.getElementById('ps-editor')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
              sx={{ flexShrink: 0 }}
            >
              See the full draft
            </Button>
          </Box>
          <Typography sx={{ fontSize: 16, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
            {statement}
          </Typography>
        </Box>
      )}

      {/* Context — collapsible card */}
      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'rgba(15,43,76,0.03)' }}>
        <CardActionArea onClick={() => setContextOpen(o => !o)}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Context — your topic and 2.1 commitments</Typography>
              <Typography variant="caption" color="text.secondary">
                Orientation only. Not the focus.
              </Typography>
            </Box>
            <ExpandMoreIcon
              sx={{ transform: contextOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: 'text.secondary' }}
            />
          </Box>
        </CardActionArea>
        <Collapse in={contextOpen} unmountOnExit>
          <CardContent sx={{ pt: 0 }}>
            {topic && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em', fontSize: '0.7rem' }}>Your research topic</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{topic}</Typography>
                {topicDesc && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{topicDesc}</Typography>}
              </Box>
            )}
            {(roughProblem || roughSolution) && (
              <>
                {roughProblem && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>Your rough problem (from 2.1)</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{roughProblem}</Typography>
                  </>
                )}
                {roughSolution && (
                  <>
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mt: 1 }}>A possible solution (from 2.1)</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{roughSolution}</Typography>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* ── Define your problem dimensions ──────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Define your problem dimensions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        Sketch the shape of the problem. Broad ideas are fine — you'll tighten constraints and
        success thresholds as you do background research.
      </Typography>

      <DraftField
        label="User + context"
        definition="Who is affected, in what real-world setting?"
        helperText="e.g., 'shelter dogs during meal time in their kennels'."
        value={userCtx}
        onChange={v => { setUserCtx(v); markStale('user_context') }}
        feedback={feedback?.user_context}
        stale={staleSet.has('user_context')}
        evaluating={evaluating === 'user_context'}
        onEvaluate={() => evaluate('user_context')}
        disabled={isReadOnly}
        rows={4}
      />

      <DraftField
        label="Need / pain"
        definition="What's broken or missing — concretely, not as an aspiration."
        helperText="e.g., 'dogs eat slowly when stressed by ambient noise'."
        value={need}
        onChange={v => { setNeed(v); markStale('need') }}
        feedback={feedback?.need}
        stale={staleSet.has('need')}
        evaluating={evaluating === 'need'}
        onEvaluate={() => evaluate('need')}
        disabled={isReadOnly}
        rows={4}
      />

      <ListField
        title="Constraints"
        definition="Things that bound the solution — what limits do you have to work within?"
        exampleHint="e.g., budget under $50, build in 2 weeks, equipment available at home/school."
        placeholder="e.g., budget under $50"
        items={constraints}
        onChange={next => { setConstraints(next); markStale('constraints') }}
        feedback={feedback?.constraints}
        stale={staleSet.has('constraints')}
        evaluating={evaluating === 'constraints'}
        onEvaluate={() => evaluate('constraints')}
        disabled={isReadOnly}
      />

      <ListField
        title="Success criteria"
        definition="What 'working' looks like — outcomes that show your build addresses the need."
        exampleHint="e.g., dogs finish meals faster than baseline, staff report less stress at feeding time."
        placeholder="e.g., dogs eat 30% faster than baseline"
        items={success}
        onChange={next => { setSuccess(next); markStale('success_criteria') }}
        feedback={feedback?.success_criteria}
        stale={staleSet.has('success_criteria')}
        evaluating={evaluating === 'success_criteria'}
        onEvaluate={() => evaluate('success_criteria')}
        disabled={isReadOnly}
      />

      <Divider sx={{ my: 3 }} />

      {/* ── Draft your problem statement ──────────────────────────────── */}
      <Typography id="ps-editor" variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Draft your problem statement</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
        Write the elevator pitch for the problem you're tackling. The shape that works:{' '}
        <em>"[User] needs [capability] in [context], because [need]."</em>{' '}
        Don't pre-commit to a specific build yet.
      </Typography>
      <DraftField
        label="Draft problem statement"
        definition="A short paragraph capturing user, context, and need — solution-space-open."
        helperText="Keeps the build open. You'll specify what you'll build in Step 5 design."
        value={statement}
        onChange={v => { setStatement(v); markStale('problem_statement') }}
        feedback={feedback?.problem_statement}
        stale={staleSet.has('problem_statement')}
        evaluating={evaluating === 'problem_statement'}
        onEvaluate={() => evaluate('problem_statement')}
        disabled={isReadOnly}
        rows={4}
      />

      <Divider sx={{ my: 3 }} />

      {/* ── How will you build and test? ──────────────────────────────── */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>How will you build and test this?</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
        What will you build to make and test your solution? Pick any that apply — specific parts
        and tools come later.
      </Typography>
      <MethodCategoryPicker
        options={METHOD_CATEGORIES_ENG}
        selected={methodCats}
        onChange={next => { setMethodCats(next); markStale('method') }}
        disabled={isReadOnly}
      />
      <DraftField
        label="Details (optional)"
        definition="Specifics if you have them — category-level is fine; exact parts come later."
        helperText="e.g., 'Arduino with sound sensors + Python to log readings, prototype tested in a kennel'."
        value={method}
        onChange={v => { setMethod(v); markStale('method') }}
        feedback={feedback?.method}
        stale={staleSet.has('method')}
        evaluating={evaluating === 'method'}
        onEvaluate={() => evaluate('method')}
        disabled={isReadOnly}
        rows={4}
      />

      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

      {/* ── Submit ─────────────────────────────────────────────────────── */}
      <SubmitBar
        isReadOnly={isReadOnly}
        disabled={!readyToSubmit()}
        submitting={submitting}
        isReturning={isReturning}
        onSubmit={() => submit(false)}
      />

      {/* Soft-gate dialog */}
      <Dialog open={!!softGate} onClose={() => setSoftGate(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit anyway?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            AI flagged a few things to think about on:
          </Typography>
          {softGate?.thinking && softGate.thinking.length > 0 && (
            <Box component="ul" sx={{ pl: 2.25, m: 0, mb: 1.5 }}>
              {softGate.thinking.map(label => (
                <li key={label}><Typography variant="body2">{label}</Typography></li>
              ))}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            You can submit anyway — your mentor will review your work regardless. Or refine first
            if you want to address these.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSoftGate(null)} disabled={submitting}>Refine first</Button>
          <Button variant="contained" onClick={() => submit(true)} disabled={submitting}>
            {submitting
              ? <><CircularProgress size={14} color="inherit" sx={{ mr: 1 }} />Submitting…</>
              : 'Submit anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// ── Main page ────────────────────────────────────────────────────────────────

type TrackKind = 'scientific_method' | 'engineering_design'

type DraftStatus = 'draft' | 'submitted' | 'approved_draft' | 'completed'

interface AIEvaluation {
  shape:          { state: 'ok' | 'caution' | 'thinking'; note: string }
  anchor:         { state: 'ok' | 'caution' | 'thinking'; note: string }
  ai_disclaimer?: string
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
type DraftField =
  | 'iv' | 'dv' | 'cv' | 'question'
  | 'user_context' | 'need' | 'constraints' | 'success_criteria' | 'problem_statement'
  | 'method'

interface DraftPayload {
  // Sci
  iv?:                string
  dv?:                string
  cv?:                string[]
  question?:          string
  // Eng
  user_context?:      string
  need?:              string
  constraints?:       string[]
  success_criteria?:  string[]
  problem_statement?: string
  // Both
  method_categories?: string[]   // what the student will build/use to collect data
  method?:            string     // free-text details
  ai_feedback?:       Partial<Record<DraftField, AIEvaluation>>
}

interface ResearchQuestionState {
  id:                     string
  track_committed:        TrackKind | null
  track_rationale:        string | null
  draft_payload:          DraftPayload | null
  status:                 DraftStatus
  mentor_note:            string | null
}

type Step2Tab = 'learn' | 'type' | 'q' | 'journal' | 'review'

export default function Step2Page() {
  const { session, studentLifecycle } = useAuth()
  const { studentId: viewerStudentId, prefixPath, loading: vasLoading } = useViewerStudent()
  const navigate = useNavigate()

  const [project,    setProject]    = useState<Project | null>(null)
  const [rq,         setRq]         = useState<ResearchQuestionState | null>(null)
  const [topic,      setTopic]      = useState<string | null>(null)
  const [topicDesc,  setTopicDesc]  = useState<string | null>(null)
  const [loading,    setLoading]    = useState(true)
  const [tab,        setTab]        = useState<Step2Tab>('type')
  const [error,      setError]      = useState<string | null>(null)
  // Only show the full-page spinner on the first load or when the student being
  // viewed actually changes. A same-student refetch (token refresh, View-As
  // no-op) must NOT unmount the form subtree, or unsaved draft input is lost.
  const loadedStudentRef = useRef<string | null>(null)

  const isTrialExpired = studentLifecycle?.lifecycle_state === 'trial_expired'

  useEffect(() => {
    async function load() {
      if (!session || vasLoading) return
      if (!viewerStudentId) { navigate('/'); return }
      if (loadedStudentRef.current !== viewerStudentId) setLoading(true)
      setError(null)
      try {
        const { data: projects, error: pErr } = await supabase
          .from('project')
          .select('*')
          .eq('student_id', viewerStudentId)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
        if (pErr) throw new Error(pErr.message)
        const proj = projects?.[0] ?? null
        setProject(proj)

        if (proj) {
          const [{ data: rqRow, error: rqErr }, { data: summary, error: sErr }] = await Promise.all([
            supabase
              .from('research_question')
              .select('id, track_committed, track_rationale, draft_payload, status, mentor_note')
              .eq('project_id', proj.project_id)
              .maybeSingle(),
            supabase
              .from('step1_summary')
              .select('confirmed_topic')
              .eq('project_id', proj.project_id)
              .maybeSingle(),
          ])
          if (rqErr) throw new Error(rqErr.message)
          if (sErr)  throw new Error(sErr.message)
          setRq((rqRow as ResearchQuestionState | null) ?? null)

          // Topic display: title prefers the AI-finalized version
          // (step1_summary.confirmed_topic) and falls back to the canonical
          // selection (research_topic → topic_recommendation.title) for
          // projects approved without step1-finalize having run. Description
          // only lives on topic_recommendation, so always pull it from there.
          let topicValue: string | null =
            (summary as { confirmed_topic: string } | null)?.confirmed_topic ?? null
          let topicDescValue: string | null = null

          const { data: rt } = await supabase
            .from('research_topic')
            .select('selected_recommendation_id')
            .eq('project_id', proj.project_id)
            .maybeSingle()
          const selectedId = (rt as { selected_recommendation_id: string | null } | null)
            ?.selected_recommendation_id
          if (selectedId) {
            const { data: rec } = await supabase
              .from('topic_recommendation')
              .select('title, description')
              .eq('id', selectedId)
              .maybeSingle()
            const recTyped = rec as { title: string; description: string | null } | null
            if (!topicValue) topicValue = recTyped?.title ?? null
            topicDescValue = recTyped?.description ?? null
          }
          setTopic(topicValue)
          setTopicDesc(topicDescValue)
        }
      } catch (e: any) {
        setError(e.message)
      } finally {
        loadedStudentRef.current = viewerStudentId
        setLoading(false)
      }
    }
    load()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, viewerStudentId, vasLoading])

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>

  // Locked: project exists but Step 1 isn't complete
  const isLocked = !project || (project.current_step ?? 1) < 2
  if (isLocked) {
    return (
      <Box sx={{ p: 4, maxWidth: 720 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <IconButton onClick={() => navigate(prefixPath('/'))} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Step 2: Research Question</Typography>
        </Box>
        <Alert severity="info" icon={<LockIcon />}>
          Complete Step 1 first to unlock this step.
        </Alert>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 720 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!project) return null

  // Tab lock state
  const trackCommitted   = rq?.track_committed ?? null
  const isSubmitted      = rq?.status === 'submitted'
  const isApprovedDraft  = rq?.status === 'approved_draft'
  const isCompleted      = rq?.status === 'completed'

  // Status badge for the header
  const statusBadge = (() => {
    if (isCompleted)     return { label: 'Approved', color: 'success' as const }
    if (isApprovedDraft) return { label: 'Draft approved — Step 3 unlocks', color: 'success' as const }
    if (isSubmitted)     return { label: 'Submitted for review', color: 'info' as const }
    if (trackCommitted)  return { label: 'Drafting your question', color: 'default' as const }
    return null
  })()

  const tab2Label = trackCommitted === 'engineering_design' ? '2.2 Problem Statement' : '2.2 Research Question'

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <IconButton onClick={() => navigate(prefixPath('/'))} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Step 2: {trackCommitted === 'engineering_design' ? 'Identify a Problem' : 'Ask a Research Question'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Narrow your topic, commit to a research direction, and lock in the question (or problem) that will drive the rest of your project.
          </Typography>
        </Box>
        {statusBadge && (
          <Chip size="small" label={statusBadge.label} color={statusBadge.color} sx={{ ml: 1 }} />
        )}
      </Box>

      {isTrialExpired && (
        <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
          Your trial has expired — you can review your existing work but new submissions are paused. Contact your office to enroll.
        </Alert>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, mt: 2 }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="Learn"        value="learn" />
        <Tab label="2.1 Research Type" value="type" />
        <Tab label={tab2Label}     value="q"    disabled={!trackCommitted} />
        <Tab label="Journal"      value="journal" />
        <Tab label="Mentor Review" value="review" />
      </Tabs>

      {tab === 'learn'   && <LearnTab />}
      {tab === 'type'    && (
        <ResearchTypeTab
          projectId={project.project_id}
          studentId={viewerStudentId!}
          topic={topic}
          topicDesc={topicDesc}
          trackCommitted={trackCommitted}
          trackRationale={rq?.track_rationale ?? null}
          onTrackCommitted={(track) => setRq(prev => prev
            ? { ...prev, track_committed: track }
            : { id: '', track_committed: track, track_rationale: null, draft_payload: null, status: 'draft', mentor_note: null }
          )}
          onRationaleSaved={(rationale) => setRq(prev => prev
            ? { ...prev, track_rationale: rationale }
            : prev
          )}
          onContinue={() => setTab('q')}
        />
      )}
      {tab === 'q'       && trackCommitted === 'scientific_method' && (
        <ResearchQuestionTabSci
          projectId={project.project_id}
          studentId={viewerStudentId!}
          topic={topic}
          topicDesc={topicDesc}
          trackRationale={rq?.track_rationale ?? null}
          draftPayload={rq?.draft_payload ?? null}
          status={rq?.status ?? 'draft'}
          mentorNote={rq?.mentor_note ?? null}
          onChange={(next) => setRq(prev => prev ? { ...prev, draft_payload: next } : prev)}
          onSubmitted={() => setRq(prev => prev ? { ...prev, status: 'submitted', mentor_note: null } : prev)}
        />
      )}
      {tab === 'q'       && trackCommitted === 'engineering_design' && (
        <ProblemStatementTabEng
          projectId={project.project_id}
          studentId={viewerStudentId!}
          topic={topic}
          topicDesc={topicDesc}
          trackRationale={rq?.track_rationale ?? null}
          draftPayload={rq?.draft_payload ?? null}
          status={rq?.status ?? 'draft'}
          mentorNote={rq?.mentor_note ?? null}
          onChange={(next) => setRq(prev => prev ? { ...prev, draft_payload: next } : prev)}
          onSubmitted={() => setRq(prev => prev ? { ...prev, status: 'submitted', mentor_note: null } : prev)}
        />
      )}
      {tab === 'journal' && <JournalTab projectId={project.project_id} studentId={viewerStudentId!} />}
      {tab === 'review'  && (
        <MentorReviewTab
          projectId={project.project_id}
          status={rq?.status ?? 'draft'}
          mentorNote={rq?.mentor_note ?? null}
        />
      )}
    </Box>
  )
}
